import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { State } from '../../shared/core/state';
import * as UserActions from '../../shared/core/state/users/user.actions';
import { selectUser } from '../../shared/core/state/users/user.selectors';
import { Subscription, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { UserService } from '../../shared/core/state/users/users.service';
import {
  SRI_LANKAN_DISTRICTS,
  SRI_LANKAN_SUBJECTS,
  SRI_LANKAN_GRADES,
  SRI_LANKA_LOCATIONS,
} from '../../shared/util/sri-lanka-data';
import { ToastService } from '@black/ui';
import { FilePondOptions } from 'filepond';
import { FilePondConfigService } from '../../shared/services/filepond-config.service';

@Component({
  selector: 'black-tutor-profile-editor',
  templateUrl: './tutor-profile-editor.component.html',
  styleUrls: ['./tutor-profile-editor.component.css'],
  standalone: false,
})
export class TutorProfileEditorComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private store = inject<Store<State>>(Store);
  private toast = inject(ToastService);
  private userService = inject(UserService);
  private filePondConfig = inject(FilePondConfigService);
  private sub = new Subscription();


  user: any = null;
  activeStep = 1;
  totalSteps = 6;

  slugChecking = false;
  slugError: string | null = null;
  slugSuggestion: string | null = null;
  slugUnique = false;
  isRefreshing = false;

  useSuggestion(suggestion: string) {
    this.tutorForm.get('slug')?.setValue(suggestion);
    this.slugError = null;
    this.slugSuggestion = null;
    this.slugUnique = true;
  }

  pondOptions: FilePondOptions = {
    allowMultiple: false,
    labelIdle:
      'Drop file here or <span class="filepond--label-action">Browse</span>',
    instantUpload: true,
  };

  bannerPondFiles: FilePondOptions['files'] = [];
  bannerPondOptions: FilePondOptions = {
    allowMultiple: false,
    labelIdle:
      '<div class="flex flex-col items-center justify-center h-full"><i class="fa-solid fa-image text-4xl mb-3 daisy-text-base-content/30"></i><span class="text-sm font-medium">Drag and drop cover banner or <span class="filepond--label-action text-primary">Browse</span></span></div>',
    instantUpload: true,
    imagePreviewHeight: 200,
    allowFileTypeValidation: true,
    acceptedFileTypes: ['image/png', 'image/jpeg'],
    stylePanelAspectRatio: '25:9',
    credits: false,
  };

  stepControls: { [key: number]: string[] } = {
    1: [
      'slug',
      'jobTitle',
      'experience',
      'biography',
      'avatar',
      'bannerImage',
      'educationalQualifications',
      'certifications',
    ],
    2: ['subjects', 'grades', 'mediums', 'classFormats'],
    3: ['districts'],
    4: ['externalClasses'],
    5: [
      'ratePerHour',
      'ratePerMonth',
      'pricingDetails',
      'whatsAppNumber',
      'youtubeChannel',
      'facebook',
      'linkedin',
      'website',
    ],
    6: ['nicFront', 'nicBack', 'academicCertificate'],
  };

  // Sri Lanka lists
  subjectsList = SRI_LANKAN_SUBJECTS;
  gradesList = SRI_LANKAN_GRADES;
  districtsList = SRI_LANKAN_DISTRICTS;
  mediumsList = ['Sinhala', 'English', 'Tamil'];
  classTypesList = ['Individual', 'Group', 'Institute'];
  sriLankaLocations = SRI_LANKA_LOCATIONS;

  // Reacting to selected physical class district for city list
  citiesForSelectedPhysicalClass: { [key: number]: string[] } = {};

  tutorForm = this.fb.group({
    // Step 1: Bio & Branding
    slug: ['', [Validators.required, Validators.pattern(/^[a-z0-9-]+$/)]],
    jobTitle: [''],
    experience: [''],
    biography: ['', [Validators.required, Validators.minLength(20)]],
    avatar: [''],
    bannerImage: [''],
    educationalQualifications: this.fb.array([]),
    certifications: this.fb.array([]),

    // Step 2: Vetting/Verification Docs
    nicFront: [''],
    nicBack: [''],
    academicCertificate: [''],

    // Step 3: Setup
    subjects: this.fb.array([]),
    grades: this.fb.array([]),
    mediums: this.fb.array([]),
    classFormats: this.fb.array([]),

    // Step 4: Locations Coverage
    districts: this.fb.array([]),

    // Step 5: External Physical Classes
    externalClasses: this.fb.array([]),

    // Step 6: Socials & Pricing
    whatsAppNumber: [
      '',
      [Validators.required, Validators.pattern(/^[0-9+() -]*$/)],
    ],
    youtubeChannel: [''],
    facebook: [''],
    linkedin: [''],
    website: [''],
    ratePerHour: [null as number | null],
    ratePerMonth: [null as number | null],
    pricingDetails: [''],
    isPublic: [false],
  });

  async ngOnInit(): Promise<void> {
    const [pondServer, bannerPondServer] = await Promise.all([
      this.filePondConfig.getServerConfig({ allowRevert: true }),
      this.filePondConfig.getServerConfig({ load: true, allowRevert: true }),
    ]);
    this.pondOptions = { ...this.pondOptions, server: pondServer };
    this.bannerPondOptions = {
      ...this.bannerPondOptions,
      server: bannerPondServer,
    };

    this.sub.add(
      this.store.select(selectUser).subscribe((user) => {
        if (user) {
          this.user = user;
          this.populateForm(user);
        }
      }),
    );

    const slugControl = this.tutorForm.get('slug');
    if (slugControl) {
      this.sub.add(
        slugControl.valueChanges
          .pipe(
            debounceTime(500),
            distinctUntilChanged(),
            switchMap((slugVal) => {
              if (!slugVal) {
                this.slugUnique = false;
                this.slugError = 'Profile slug is required';
                return of(null);
              }
              if (slugControl.invalid) {
                this.slugUnique = false;
                this.slugError =
                  'Invalid format. Use lowercase letters, numbers, and hyphens.';
                return of(null);
              }
              if (
                slugVal === this.user?.slug ||
                slugVal === this.user?.pendingTutorProfileUpdates?.slug
              ) {
                this.slugUnique = true;
                this.slugError = null;
                this.slugSuggestion = null;
                return of(null);
              }
              this.slugChecking = true;
              return this.userService.validateSlug(slugVal);
            }),
          )
          .subscribe({
            next: (res: any) => {
              this.slugChecking = false;
              if (res) {
                this.slugUnique = res.unique;
                if (res.unique) {
                  this.slugError = null;
                  this.slugSuggestion = null;
                } else {
                  this.slugError = 'This URL slug is already taken.';
                  this.slugSuggestion = res.suggestion;
                }
              }
            },
            error: (err) => {
              this.slugChecking = false;
              this.slugUnique = false;
              this.slugSuggestion = null;
              this.slugError =
                err?.error?.message || 'Failed to validate URL slug.';
            },
          }),
      );
    }
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  refreshProfileStatus(): void {
    if (this.isRefreshing) return;
    this.isRefreshing = true;
    this.userService.getUserInfo().subscribe({
      next: (user) => {
        this.isRefreshing = false;
        this.store.dispatch(
          UserActions.updateUserProfileSuccess({ user, redirect: false }),
        );
        this.toast.showToast('Profile status refreshed', 'success');
      },
      error: (err) => {
        this.isRefreshing = false;
        this.toast.showToast(
          err.error?.message || 'Failed to refresh profile status',
          'error',
        );
      },
    });
  }

  // Getters for FormArrays
  get educationalQualifications() {
    return this.tutorForm.get('educationalQualifications') as FormArray;
  }

  get certifications() {
    return this.tutorForm.get('certifications') as FormArray;
  }

  get subjects() {
    return this.tutorForm.get('subjects') as FormArray;
  }

  get grades() {
    return this.tutorForm.get('grades') as FormArray;
  }

  get mediums() {
    return this.tutorForm.get('mediums') as FormArray;
  }

  get classFormats() {
    return this.tutorForm.get('classFormats') as FormArray;
  }

  get districts() {
    return this.tutorForm.get('districts') as FormArray;
  }

  get externalClasses() {
    return this.tutorForm.get('externalClasses') as FormArray;
  }

  // Form Population
  populateForm(user: any) {
    const profile = user.profile || {};
    const tutorInfo = {
      ...(profile.tutorInfo || {}),
      ...(user.pendingTutorProfileUpdates?.tutorInfo || {}),
    };
    const personal = {
      ...(profile.personal || {}),
      ...(user.pendingTutorProfileUpdates?.personal || {}),
    };
    const professional = {
      ...(profile.professional || {}),
      ...(user.pendingTutorProfileUpdates?.professional || {}),
    };

    // Clear existing arrays
    this.educationalQualifications.clear();
    this.certifications.clear();
    this.subjects.clear();
    this.grades.clear();
    this.mediums.clear();
    this.classFormats.clear();
    this.districts.clear();
    this.externalClasses.clear();

    let defaultSlug = '';
    if (!user.slug && personal.firstName) {
      defaultSlug = `${personal.firstName} ${personal.lastName || ''}`
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)+/g, '');
    }

    const initialSlug =
      user.pendingTutorProfileUpdates?.slug || user.slug || defaultSlug;

    // Populate standard values
    this.tutorForm.patchValue({
      slug: initialSlug,
      jobTitle: professional.jobTitle || '',
      experience: professional.experience || '',
      biography: personal.biography || '',
      avatar: personal.avatar?._id || personal.avatar || '',
      bannerImage: tutorInfo.bannerImage?._id || tutorInfo.bannerImage || '',
      nicFront:
        tutorInfo.verificationDocuments?.nicFront?._id ||
        tutorInfo.verificationDocuments?.nicFront ||
        '',
      nicBack:
        tutorInfo.verificationDocuments?.nicBack?._id ||
        tutorInfo.verificationDocuments?.nicBack ||
        '',
      academicCertificate:
        tutorInfo.verificationDocuments?.academicCertificate?._id ||
        tutorInfo.verificationDocuments?.academicCertificate ||
        '',
      whatsAppNumber: tutorInfo.whatsAppNumber || personal.mobile || '',
      youtubeChannel: tutorInfo.youtubeChannel || '',
      facebook: tutorInfo.socialLinks?.facebook || '',
      linkedin: tutorInfo.socialLinks?.linkedin || '',
      website: tutorInfo.socialLinks?.website || '',
      ratePerHour: tutorInfo.ratePerHour || null,
      ratePerMonth: tutorInfo.ratePerMonth || null,
      pricingDetails: tutorInfo.pricingDetails || '',
      isPublic: tutorInfo.isPublic !== undefined ? tutorInfo.isPublic : false,
    });

    if (initialSlug && /^[a-z0-9-]+$/.test(initialSlug)) {
      this.slugUnique = true;
      this.slugError = null;
      this.slugSuggestion = null;
    } else if (!initialSlug) {
      this.slugUnique = false;
      this.slugError = 'Profile slug is required';
    }

    if (tutorInfo.bannerImage) {
      const bannerId = tutorInfo.bannerImage._id || tutorInfo.bannerImage;
      this.bannerPondFiles = [
        {
          source: bannerId,
          options: { type: 'local' },
        },
      ];
    }

    // Qualifications & Certifications
    const eduQuals = professional.educationalQualifications || [];
    eduQuals.forEach((qual: string) =>
      this.educationalQualifications.push(this.fb.control(qual)),
    );

    const certs = professional.certifications || [];
    certs.forEach((cert: string) =>
      this.certifications.push(this.fb.control(cert)),
    );

    // Multi-selects
    const subs = tutorInfo.subjects || [];
    subs.forEach((sub: string) => this.subjects.push(this.fb.control(sub)));

    const grds = tutorInfo.grades || [];
    grds.forEach((grd: string) => this.grades.push(this.fb.control(grd)));

    const meds = tutorInfo.mediums || [];
    meds.forEach((med: string) => this.mediums.push(this.fb.control(med)));

    const fmts = tutorInfo.classFormats || [];
    fmts.forEach((fmt: string) => this.classFormats.push(this.fb.control(fmt)));

    const dists = tutorInfo.districts || [];
    dists.forEach((dist: string) => this.districts.push(this.fb.control(dist)));

    // External classes
    const extClasses = tutorInfo.externalClasses || [];
    extClasses.forEach((cls: any, index: number) => {
      const clsForm = this.fb.group({
        title: [cls.title || '', Validators.required],
        subject: [cls.subject || '', Validators.required],
        medium: [cls.medium || 'ENGLISH', Validators.required],
        grade: [cls.grade || '', Validators.required],
        classType: [cls.classType || 'Individual', Validators.required],
        location: this.fb.group({
          district: [cls.location?.district || '', Validators.required],
          city: [cls.location?.city || '', Validators.required],
          venue: [cls.location?.venue || ''],
        }),
        schedule: [cls.schedule || ''],
        feeDetails: [cls.feeDetails || ''],
      });

      this.externalClasses.push(clsForm);
      this.updateCitiesForClass(index, cls.location?.district);
    });
  }

  // Helper arrays additions
  addSubject() {
    this.subjects.push(this.fb.control(''));
  }

  removeSubject(index: number) {
    this.subjects.removeAt(index);
  }

  addQualification() {
    this.educationalQualifications.push(this.fb.control(''));
  }

  removeQualification(index: number) {
    this.educationalQualifications.removeAt(index);
  }

  addCertification() {
    this.certifications.push(this.fb.control(''));
  }

  removeCertification(index: number) {
    this.certifications.removeAt(index);
  }

  addExternalClass() {
    const clsForm = this.fb.group({
      title: ['', Validators.required],
      subject: ['', Validators.required],
      medium: ['ENGLISH', Validators.required],
      grade: ['', Validators.required],
      classType: ['Individual', Validators.required],
      location: this.fb.group({
        district: ['', Validators.required],
        city: ['', Validators.required],
        venue: [''],
      }),
      schedule: [''],
      feeDetails: [''],
    });
    this.externalClasses.push(clsForm);
  }

  removeExternalClass(index: number) {
    this.externalClasses.removeAt(index);
    delete this.citiesForSelectedPhysicalClass[index];
  }

  onPhysicalClassDistrictChange(index: number, event: any) {
    const districtName = event.target.value;
    this.updateCitiesForClass(index, districtName);

    // Reset City value
    const clsForm = this.externalClasses.at(index) as FormGroup;
    clsForm.get('location.city')?.setValue('');
  }

  updateCitiesForClass(index: number, districtName: string) {
    if (!districtName) {
      this.citiesForSelectedPhysicalClass[index] = [];
      return;
    }

    // Default to empty to avoid stale values if the district is not found
    this.citiesForSelectedPhysicalClass[index] = [];

    // Find in sriLankaLocations
    for (const prov of this.sriLankaLocations) {
      const dist = prov.districts.find((d) => d.name === districtName);
      if (dist) {
        this.citiesForSelectedPhysicalClass[index] = dist.cities;
        break;
      }
    }
  }

  // Checkbox handlers
  onCheckboxChange(arrayName: string, value: string, event: any) {
    const array = this.tutorForm.get(arrayName) as FormArray;
    if (event.target.checked) {
      array.push(this.fb.control(value));
    } else {
      const index = array.controls.findIndex((x) => x.value === value);
      if (index !== -1) array.removeAt(index);
    }
  }

  isChecked(arrayName: string, value: string): boolean {
    const array = this.tutorForm.get(arrayName) as FormArray;
    return array.value.includes(value);
  }

  // Image upload callbacks
  onAvatarUploadSuccess(resourceId: string) {
    this.tutorForm.controls.avatar.setValue(resourceId);
  }

  onBannerUploadSuccess(event: any) {
    this.tutorForm.controls.bannerImage.setValue(event.file.serverId);
  }

  onNicFrontUploadSuccess(event: any) {
    this.tutorForm.controls.nicFront.setValue(event.file.serverId);
  }

  onNicBackUploadSuccess(event: any) {
    this.tutorForm.controls.nicBack.setValue(event.file.serverId);
  }

  onAcademicCertUploadSuccess(event: any) {
    this.tutorForm.controls.academicCertificate.setValue(event.file.serverId);
  }

  getValidSubjectsCount(): number {
    return this.subjects.controls.filter(
      (ctrl) => ctrl.value && ctrl.value.trim().length > 0,
    ).length;
  }

  step2Attempted = false;

  // Navigation
  nextStep() {
    const controls = this.stepControls[this.activeStep];
    let isStepValid = true;

    if (controls) {
      controls.forEach((ctrlName) => {
        const ctrl = this.tutorForm.get(ctrlName);
        if (ctrl) {
          ctrl.markAllAsTouched();
          if (ctrl.invalid) {
            isStepValid = false;
          }
        }
      });
    }

    // Step 1 Validation
    if (this.activeStep === 1) {
      if (this.slugChecking) {
        this.toast.showToast(
          'Checking URL slug availability. Please wait...',
          'warning',
        );
        return;
      }
      if (this.slugError || !this.slugUnique) {
        this.toast.showToast(
          'Please enter a valid and available profile URL slug.',
          'error',
        );
        return;
      }
      if (this.tutorForm.get('biography')?.invalid) {
        this.toast.showToast(
          'Instructor biography is required (minimum 20 characters).',
          'error',
        );
        return;
      }
    }

    // Step 2 Validation
    if (this.activeStep === 2) {
      this.step2Attempted = true;
      if (this.getValidSubjectsCount() === 0) {
        this.toast.showToast(
          'Please add at least one subject you teach before proceeding.',
          'error',
        );
        return;
      }
    }

    // Step 4 Validation
    if (this.activeStep === 4) {
      if (this.externalClasses.length > 0) {
        this.externalClasses.markAllAsTouched();
        if (this.externalClasses.invalid) {
          this.toast.showToast(
            'Please fill in all mandatory details for your listed physical classes.',
            'error',
          );
          return;
        }
      }
    }

    // Step 5 Validation
    if (this.activeStep === 5) {
      if (this.tutorForm.get('whatsAppNumber')?.invalid) {
        this.toast.showToast(
          'A valid WhatsApp contact number is required.',
          'error',
        );
        return;
      }
    }

    if (!isStepValid) {
      this.toast.showToast(
        'Please complete all required fields in this step before proceeding.',
        'error',
      );
      return;
    }

    if (this.activeStep < this.totalSteps) {
      this.activeStep++;
    }
  }

  prevStep() {
    if (this.activeStep > 1) {
      this.activeStep--;
    }
  }

  saveProfile() {
    this.tutorForm.markAllAsTouched();

    if (this.slugChecking) {
      this.toast.showToast(
        'Checking URL slug availability. Please wait...',
        'warning',
      );
      return;
    }

    if (this.slugError || !this.slugUnique) {
      this.toast.showToast('Please provide a valid and available profile URL slug.', 'error');
      return;
    }

    if (this.getValidSubjectsCount() === 0) {
      this.toast.showToast('Please add at least one subject you teach.', 'error');
      return;
    }

    if (this.tutorForm.invalid) {
      this.toast.showToast('Please fix all errors before saving!', 'error');
      return;
    }

    const val = this.tutorForm.value;

    const tutorInfoPayload = {
      slug: val.slug || undefined,
      subjects: val.subjects,
      grades: val.grades,
      mediums: val.mediums,
      classFormats: val.classFormats,
      districts: val.districts,
      ratePerHour: val.ratePerHour,
      ratePerMonth: val.ratePerMonth,
      pricingDetails: val.pricingDetails,
      whatsAppNumber: val.whatsAppNumber,
      youtubeChannel: val.youtubeChannel,
      socialLinks: {
        facebook: val.facebook,
        linkedin: val.linkedin,
        website: val.website,
      },
      bannerImage: val.bannerImage || undefined,
      verificationDocuments: {
        nicFront: val.nicFront || undefined,
        nicBack: val.nicBack || undefined,
        academicCertificate: val.academicCertificate || undefined,
      },
      externalClasses: val.externalClasses,
      isPublic: val.isPublic,

      // Bio, branding, and professional fields for approval
      biography: val.biography || undefined,
      avatar: val.avatar || undefined,
      jobTitle: val.jobTitle || undefined,
      experience: val.experience || undefined,
      educationalQualifications: val.educationalQualifications || [],
      certifications: val.certifications || [],
    };

    // Update the tutor profile settings (which handles all fields for approval)
    this.store.dispatch(
      UserActions.updateTutorProfile({
        tutorInfo: tutorInfoPayload,
      }),
    );
  }
}
